/**
 * Cross-instrument debt rollup.
 *
 * Normalises every kind of debt Atlas knows about (amortizing term
 * loans, the SBA draw facility, revolving credit cards) into a single
 * `DebtRow` shape so the Debt Balances page can total, sort, and
 * compare them side by side. All money is integer cents; all dates
 * are ISO 'YYYY-MM-DD' strings, matching `loan-math.ts`.
 *
 * Everything here is pure — no React, no I/O — so it can be unit
 * tested and reused by future surfaces (dashboard tiles, projections).
 */

import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  addMonthsOnDay,
  buildSchedule,
  computeMonthlyPaymentCents,
  isoToLocalDate,
  type LoanConfig,
  type ScheduledPayment,
} from './loan-math';
import type { PaymentRecords } from './loan-records';
import type { SBALoanDrawConfig } from './SBALoanDraws';

export type DebtKind = 'term-loan' | 'sba-loan' | 'credit-card';

export type DebtStatusTone = 'success' | 'info' | 'warning' | 'muted';

export interface DebtStatus {
  label: string;
  tone: DebtStatusTone;
}

/**
 * Inputs the payoff planner needs to re-amortize a loan with an extra
 * monthly principal payment. Only amortizing instruments carry this;
 * revolving credit has no fixed payment to accelerate.
 */
export interface DebtPlannerTerms {
  /** Scheduled P&I payment once amortization is underway. */
  paymentCents: number;
  /** Due date of the first payment the planner should simulate. */
  startDate: string;
  /** Day-of-month for subsequent payments (1–28). */
  dayOfMonth: number;
}

export interface DebtRow {
  id: string;
  name: string;
  lender: string | null;
  kind: DebtKind;
  kindLabel: string;
  /** Where the balance comes from, so the operator knows how fresh it is. */
  source: 'Ledger' | 'Mercury' | 'Plaid' | 'Bank feed';
  /** Annual rate as a decimal, or null when the feed doesn't expose it. */
  annualRate: number | null;
  /** Original principal / face value / credit limit. */
  originalCents: number | null;
  /** Principal currently outstanding. */
  balanceCents: number;
  /** Undrawn facility or available credit. */
  availableCents: number | null;
  monthlyPaymentCents: number | null;
  monthlyPaymentHint: string | null;
  nextDueDate: string | null;
  nextPaymentCents: number | null;
  nextPaymentHint: string | null;
  /** Interest still to be paid over the remaining life (null when unknown). */
  interestRemainingCents: number | null;
  payoffDate: string | null;
  status: DebtStatus;
  href: string | null;
  planner: DebtPlannerTerms | null;
}

export interface UpcomingPayment {
  id: string;
  debtId: string;
  debtName: string;
  dueDate: string;
  amountCents: number;
  principalCents: number | null;
  interestCents: number | null;
  /** True when the amount is our projection rather than a scheduled figure. */
  estimated: boolean;
  overdue: boolean;
  label: string;
}

export interface DebtRollupResult {
  row: DebtRow;
  upcoming: UpcomingPayment[];
}

interface RollupOptions {
  /** ISO date treated as "today". */
  today: string;
  /** ISO date; upcoming payments are collected up to and including this day. */
  horizon: string;
}

/**
 * Amortizing term loan (e.g. the family promissory note). Paid status
 * comes from the ledger's payment records — anything not marked paid is
 * still owed, including past-due rows.
 */
export function rollupTermLoan(
  config: LoanConfig,
  slug: string,
  records: PaymentRecords,
  href: string,
  opts: RollupOptions,
): DebtRollupResult {
  const schedule = buildSchedule(config);
  const unpaid = schedule.filter((p) => !records[p.number]?.paidOn);

  const balanceCents = unpaid.reduce((sum, p) => sum + p.principalCents, 0);
  const interestRemainingCents = unpaid.reduce((sum, p) => sum + p.interestCents, 0);
  const next = unpaid[0] ?? null;
  const overdueCount = unpaid.filter((p) => p.dueDate < opts.today).length;
  const paidCount = schedule.length - unpaid.length;

  let status: DebtStatus;
  if (schedule.length > 0 && unpaid.length === 0) {
    status = { label: 'Paid in full', tone: 'success' };
  } else if (overdueCount > 0) {
    status = {
      label: `${overdueCount} payment${overdueCount === 1 ? '' : 's'} not marked paid`,
      tone: 'warning',
    };
  } else if (opts.today < config.effectiveDate) {
    status = { label: 'Not yet started', tone: 'muted' };
  } else {
    status = { label: `Current · ${paidCount} of ${schedule.length} paid`, tone: 'info' };
  }

  const upcoming: UpcomingPayment[] = unpaid
    .filter((p) => p.dueDate <= opts.horizon)
    .map((p) => ({
      id: `${slug}-${p.number}`,
      debtId: slug,
      debtName: config.label,
      dueDate: p.dueDate,
      amountCents: p.paymentCents,
      principalCents: p.principalCents,
      interestCents: p.interestCents,
      estimated: false,
      overdue: p.dueDate < opts.today,
      label: `Payment #${p.number} of ${schedule.length}`,
    }));

  return {
    row: {
      id: slug,
      name: config.label,
      lender: config.lender ?? null,
      kind: 'term-loan',
      kindLabel: 'Term loan',
      source: 'Ledger',
      annualRate: config.annualRate,
      originalCents: config.principalCents,
      balanceCents,
      availableCents: null,
      monthlyPaymentCents: schedule[0]?.paymentCents ?? null,
      monthlyPaymentHint: 'Fixed P&I',
      nextDueDate: next?.dueDate ?? null,
      nextPaymentCents: next?.paymentCents ?? null,
      nextPaymentHint: next ? `Payment #${next.number}` : null,
      interestRemainingCents,
      payoffDate: schedule[schedule.length - 1]?.dueDate ?? null,
      status,
      href,
      planner:
        next && balanceCents > 0
          ? {
              paymentCents: next.paymentCents,
              startDate: next.dueDate,
              dayOfMonth: config.paymentDayOfMonth,
            }
          : null,
    },
    upcoming,
  };
}

/**
 * SBA facility with a draw period. During the draw window the lender
 * bills interest-only on the drawn balance; once it closes, the drawn
 * balance amortizes over `termMonths`. Because the SBA ledger doesn't
 * track individual payments yet, post-draw rows assume on-time payment.
 */
export function rollupSbaLoan(
  config: SBALoanDrawConfig,
  slug: string,
  href: string,
  opts: RollupOptions,
): DebtRollupResult {
  const drawnCents = config.draws.reduce((sum, d) => sum + d.amountCents, 0);
  const budgetCents = config.buckets.reduce((sum, b) => sum + b.budgetCents, 0);
  const availableCents = Math.max(0, budgetCents - drawnCents);

  const drawDay = isoToLocalDate(config.closingDate).getDate();
  const drawPeriodEnd = addMonthsOnDay(config.closingDate, config.drawPeriodMonths, drawDay);
  const drawPeriodOpen = opts.today < drawPeriodEnd;

  // Post-draw amortization against what was actually drawn. If the
  // operator draws more before the window closes this re-computes
  // automatically on the next render.
  const amortConfig: LoanConfig = {
    label: config.label,
    lender: config.lender,
    principalCents: drawnCents,
    annualRate: config.annualRate,
    termMonths: config.termMonths,
    effectiveDate: drawPeriodEnd,
    firstPaymentDate: addMonthsOnDay(drawPeriodEnd, 1, drawDay),
    paymentDayOfMonth: drawDay,
  };
  const amortSchedule = buildSchedule(amortConfig);
  const estPiCents = computeMonthlyPaymentCents(
    drawnCents,
    config.annualRate,
    config.termMonths,
  );

  const upcoming: UpcomingPayment[] = [];

  if (drawPeriodOpen) {
    const monthlyInterestCents = Math.round((drawnCents * config.annualRate) / 12);

    // Interest-only bills land on each monthly anniversary of closing.
    const interestDates = monthlyAnniversariesAfter(
      config.closingDate,
      drawDay,
      opts.today,
      drawPeriodEnd,
    );
    const nextInterestDate = interestDates[0] ?? drawPeriodEnd;
    const monthsLeftInDraw = interestDates.length;

    for (const d of interestDates) {
      if (d > opts.horizon) break;
      upcoming.push({
        id: `${slug}-interest-${d}`,
        debtId: slug,
        debtName: config.label,
        dueDate: d,
        amountCents: monthlyInterestCents,
        principalCents: 0,
        interestCents: monthlyInterestCents,
        estimated: true,
        overdue: false,
        label: 'Interest-only (draw period)',
      });
    }
    for (const p of amortSchedule) {
      if (p.dueDate > opts.horizon) break;
      upcoming.push(scheduledToUpcoming(p, slug, config.label, amortSchedule.length, true, opts.today));
    }

    const daysLeft = daysBetween(opts.today, drawPeriodEnd);
    const interestRemainingCents =
      monthsLeftInDraw * monthlyInterestCents +
      amortSchedule.reduce((sum, p) => sum + p.interestCents, 0);

    return {
      row: {
        id: slug,
        name: config.label,
        lender: config.lender ?? null,
        kind: 'sba-loan',
        kindLabel: 'SBA loan · draw period',
        source: 'Ledger',
        annualRate: config.annualRate,
        originalCents: config.facePrincipalCents,
        balanceCents: drawnCents,
        availableCents,
        monthlyPaymentCents: monthlyInterestCents,
        monthlyPaymentHint: `Interest-only until ${formatDate(isoToLocalDate(drawPeriodEnd))} · then ~${formatCurrency(estPiCents, { whole: true })} P&I`,
        nextDueDate: nextInterestDate,
        nextPaymentCents: monthlyInterestCents,
        nextPaymentHint: 'Est. interest on drawn balance',
        interestRemainingCents,
        payoffDate: amortSchedule[amortSchedule.length - 1]?.dueDate ?? null,
        status: {
          label: `Draw period · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
          tone: 'info',
        },
        href,
        planner:
          drawnCents > 0 && amortSchedule.length > 0
            ? {
                paymentCents: estPiCents,
                startDate: amortConfig.firstPaymentDate,
                dayOfMonth: drawDay,
              }
            : null,
      },
      upcoming,
    };
  }

  // Draw period closed: treat every row due before today as settled on
  // time (no SBA payment records yet) and the rest as outstanding.
  const remaining = amortSchedule.filter((p) => p.dueDate >= opts.today);
  const balanceCents = remaining.reduce((sum, p) => sum + p.principalCents, 0);
  const interestRemainingCents = remaining.reduce((sum, p) => sum + p.interestCents, 0);
  const next = remaining[0] ?? null;

  for (const p of remaining) {
    if (p.dueDate > opts.horizon) break;
    upcoming.push(scheduledToUpcoming(p, slug, config.label, amortSchedule.length, true, opts.today));
  }

  return {
    row: {
      id: slug,
      name: config.label,
      lender: config.lender ?? null,
      kind: 'sba-loan',
      kindLabel: 'SBA loan · amortizing',
      source: 'Ledger',
      annualRate: config.annualRate,
      originalCents: config.facePrincipalCents,
      balanceCents,
      availableCents: 0,
      monthlyPaymentCents: estPiCents,
      monthlyPaymentHint: 'Est. P&I on drawn balance',
      nextDueDate: next?.dueDate ?? null,
      nextPaymentCents: next?.paymentCents ?? null,
      nextPaymentHint: next ? `Payment #${next.number} (est.)` : null,
      interestRemainingCents,
      payoffDate: amortSchedule[amortSchedule.length - 1]?.dueDate ?? null,
      status:
        remaining.length === 0
          ? { label: 'Paid in full', tone: 'success' }
          : { label: 'Amortizing · assumes on-time payments', tone: 'info' },
      href,
      planner:
        next && balanceCents > 0
          ? { paymentCents: next.paymentCents, startDate: next.dueDate, dayOfMonth: drawDay }
          : null,
    },
    upcoming,
  };
}

/**
 * Revolving credit account synced from the bank feed. Rates and
 * statement due dates aren't exposed by Mercury / Plaid, so those
 * columns stay null and the page renders an em-dash.
 */
export function rollupCreditAccount(input: {
  id: string;
  name: string;
  institution: string | null;
  provider: string | null;
  currentBalanceCents: number;
  availableBalanceCents: number | null;
}): DebtRow {
  const balanceCents = Math.max(0, input.currentBalanceCents);
  const availableCents = input.availableBalanceCents;
  const limitCents =
    availableCents != null ? balanceCents + Math.max(0, availableCents) : null;
  const utilization =
    limitCents && limitCents > 0 ? balanceCents / limitCents : null;

  let status: DebtStatus;
  if (balanceCents === 0) {
    status = { label: 'Paid off', tone: 'success' };
  } else if (utilization != null && utilization >= 0.7) {
    status = { label: `Revolving · ${Math.round(utilization * 100)}% utilized`, tone: 'warning' };
  } else if (utilization != null) {
    status = { label: `Revolving · ${Math.round(utilization * 100)}% utilized`, tone: 'info' };
  } else {
    status = { label: 'Revolving', tone: 'info' };
  }

  const provider = (input.provider ?? '').toLowerCase();
  const source: DebtRow['source'] =
    provider === 'mercury' ? 'Mercury' : provider === 'plaid' ? 'Plaid' : 'Bank feed';

  return {
    id: `credit-${input.id}`,
    name: input.name,
    lender: input.institution,
    kind: 'credit-card',
    kindLabel: 'Credit card',
    source,
    annualRate: null,
    originalCents: limitCents,
    balanceCents,
    availableCents,
    monthlyPaymentCents: null,
    monthlyPaymentHint: 'Statement balance · varies',
    nextDueDate: null,
    nextPaymentCents: null,
    nextPaymentHint: 'Per card statement',
    interestRemainingCents: null,
    payoffDate: null,
    status,
    href: '/financials/bank-accounts',
    planner: null,
  };
}

export interface PayoffSimulation {
  months: number;
  totalInterestCents: number;
  payoffDate: string;
}

/**
 * Re-amortize `balanceCents` at `annualRate` paying `paymentCents +
 * extraCents` each month. Returns null when the payment doesn't even
 * cover a month of interest (the balance would never shrink).
 */
export function simulatePayoff(
  balanceCents: number,
  annualRate: number,
  paymentCents: number,
  extraCents: number,
  terms: Pick<DebtPlannerTerms, 'startDate' | 'dayOfMonth'>,
  maxMonths = 1200,
): PayoffSimulation | null {
  if (balanceCents <= 0) {
    return { months: 0, totalInterestCents: 0, payoffDate: terms.startDate };
  }
  const r = annualRate / 12;
  const pay = paymentCents + Math.max(0, extraCents);
  if (pay <= Math.round(balanceCents * r)) return null;

  let bal = balanceCents;
  let months = 0;
  let totalInterestCents = 0;
  while (bal > 0 && months < maxMonths) {
    const interest = Math.round(bal * r);
    const principal = Math.min(bal, pay - interest);
    bal -= principal;
    totalInterestCents += interest;
    months += 1;
  }
  if (bal > 0) return null;

  const payoffDate =
    months <= 1
      ? terms.startDate
      : addMonthsOnDay(terms.startDate, months - 1, terms.dayOfMonth);
  return { months, totalInterestCents, payoffDate };
}

/** Balance-weighted average rate across rows that expose a rate. */
export function weightedAverageRate(rows: DebtRow[]): number | null {
  let weighted = 0;
  let total = 0;
  for (const r of rows) {
    if (r.annualRate == null || r.balanceCents <= 0) continue;
    weighted += r.annualRate * r.balanceCents;
    total += r.balanceCents;
  }
  return total > 0 ? weighted / total : null;
}

export function addDaysISO(iso: string, days: number): string {
  const d = isoToLocalDate(iso);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysBetween(fromISO: string, toISO: string): number {
  return Math.max(
    0,
    Math.round(
      (isoToLocalDate(toISO).getTime() - isoToLocalDate(fromISO).getTime()) /
        (1000 * 60 * 60 * 24),
    ),
  );
}

/**
 * Monthly anniversaries of `anchorISO` (snapped to `day`) that fall
 * strictly after `afterISO` and on or before `untilISO`, ascending.
 */
function monthlyAnniversariesAfter(
  anchorISO: string,
  day: number,
  afterISO: string,
  untilISO: string,
): string[] {
  const out: string[] = [];
  for (let i = 1; i < 600; i++) {
    const d = addMonthsOnDay(anchorISO, i, day);
    if (d > untilISO) break;
    if (d > afterISO) out.push(d);
  }
  return out;
}

function scheduledToUpcoming(
  p: ScheduledPayment,
  slug: string,
  debtName: string,
  total: number,
  estimated: boolean,
  today: string,
): UpcomingPayment {
  return {
    id: `${slug}-${p.number}`,
    debtId: slug,
    debtName,
    dueDate: p.dueDate,
    amountCents: p.paymentCents,
    principalCents: p.principalCents,
    interestCents: p.interestCents,
    estimated,
    overdue: p.dueDate < today,
    label: `Payment #${p.number} of ${total}${estimated ? ' (est.)' : ''}`,
  };
}
